import React from 'react';
import { View, Pressable, StyleSheet, Text } from 'react-native';
import { useTranslation } from 'react-i18next';
import { BottomSheet } from '../../weekly/v2/primitives/BottomSheet';
import { PantryIcon } from '../PantryIcon';
import { ZONE_META, ZONE_KEYS } from '../zoneMeta';
import { Paprika, PaprikaZones } from '../../../styles/theme';
import { PantryLocation } from '../../../utils/pantryLocation';

interface Props {
  visible: boolean;
  onClose: () => void;
  onZone: (zone: PantryLocation) => void;
  onReceipt: () => void;
}

export function AddWhereSheet({ visible, onClose, onZone, onReceipt }: Props) {
  const { t } = useTranslation();

  const handleZone = (zone: PantryLocation) => {
    onZone(zone);
    onClose();
  };

  const handleReceipt = () => {
    onReceipt();
    onClose();
  };

  return (
    <BottomSheet visible={visible} onClose={onClose}>
      <View style={styles.container}>
        {/* Title */}
        <Text style={styles.title}>{t('pantry.add.whereTitle')}</Text>

        {/* 2x2 Zone grid */}
        <View style={styles.zoneGrid}>
          {ZONE_KEYS.map((zone) => {
            const meta = ZONE_META[zone];
            const zoneColors = PaprikaZones[zone];

            return (
              <Pressable
                key={zone}
                style={[
                  styles.zoneButton,
                  {
                    backgroundColor: zoneColors.tint,
                    borderColor: `${zoneColors.accent}33`,
                  },
                ]}
                onPress={() => handleZone(zone)}
              >
                <View
                  style={[
                    styles.zoneIcon,
                    {
                      backgroundColor: Paprika.glassWhite,
                    },
                  ]}
                >
                  <PantryIcon icon={meta.icon} size={20} color={zoneColors.accent} />
                </View>
                <Text style={[styles.zoneLabel, { color: Paprika.textPrimary }]}>
                  {t(meta.labelKey)}
                </Text>
              </Pressable>
            );
          })}
        </View>

        {/* Receipt row */}
        <Pressable
          style={[styles.receiptButton, { backgroundColor: Paprika.brandTint, borderColor: `${Paprika.accent}33` }]}
          onPress={handleReceipt}
        >
          <View style={[styles.receiptIcon, { backgroundColor: Paprika.surfaceLowest }]}>
            <PantryIcon
              icon={{ lib: 'mci', name: 'file-document-outline' }}
              size={19}
              color={Paprika.accent}
            />
          </View>
          <View style={styles.receiptText}>
            <Text style={styles.receiptLabel}>{t('pantry.add.receiptLong')}</Text>
            <Text style={styles.receiptSub}>{t('pantry.add.receiptAuto')}</Text>
          </View>
          <View style={styles.chevronRight}>
            <PantryIcon
              icon={{ lib: 'mci', name: 'chevron-right' }}
              size={16}
              color={Paprika.accent}
            />
          </View>
        </Pressable>
      </View>
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
  },
  title: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.05,
    textTransform: 'uppercase',
    color: Paprika.textMuted,
    marginBottom: 12,
  },
  zoneGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  zoneButton: {
    width: '48%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 11,
    paddingVertical: 14,
    paddingHorizontal: 13,
    borderRadius: 14,
    borderWidth: 1,
  },
  zoneIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 0,
  },
  zoneLabel: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13.5,
    fontWeight: '600',
  },
  receiptButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 11,
    marginTop: 12,
    paddingVertical: 13,
    paddingHorizontal: 14,
    borderRadius: 14,
    borderWidth: 1,
  },
  receiptIcon: {
    width: 34,
    height: 34,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 0,
  },
  receiptText: {
    flex: 1,
  },
  receiptLabel: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13.5,
    fontWeight: '600',
    color: Paprika.textPrimary,
  },
  receiptSub: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: Paprika.textMuted,
    marginTop: 1,
  },
  chevronRight: {
    flex: 0,
  },
});
